"use client";

import type { EmojiClickData, EmojiStyle } from "emoji-picker-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { type CSSProperties, Suspense, useEffect, useMemo, useRef } from "react";
import { lazyChunk } from "@/lib/lazy-chunk";
import { cn } from "@/lib/utils";
import { buildEmojiCategories } from "./emoji-categories";
import { restoreFocusIfLost } from "./restore-focus-if-lost";

// `emoji-picker-react` is ~420 KB — by far the widget's heaviest
// dependency, and the visitor only ever needs it if they open the
// picker. `lazy()` splits it into its own chunk that's fetched on
// first open; `EMOJI_STYLE_NATIVE` lets us reference the enum value
// without a runtime import of the whole module.
const EmojiPickerReact = lazyChunk("emoji-picker", () => import("emoji-picker-react"));
const EMOJI_STYLE_NATIVE = "native" as EmojiStyle;

interface EmojiPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (emoji: string) => void;
  /** Optional anchor — clicks outside this element close the picker. */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

/**
 * `emoji-picker-react` exposes its surface via `--epr-*` CSS vars.
 * We remap them to the widget's own `--wx-*` tokens so the picker
 * inherits brand background, foreground, border, primary, etc., and
 * flips dark/light alongside the rest of the chrome (the wx tokens
 * already pivot off `[data-theme="dark"]`) — no JS theme switch.
 *
 * **Why apply via the lib's own `style` prop (not the wrapper)**:
 * the lib injects its own stylesheet that sets the `--epr-*`
 * defaults directly on the `.EmojiPickerReact` element. CSS vars
 * defined on the element itself ALWAYS beat inherited ones from a
 * parent — so setting them on our `motion.div` wrapper had no
 * effect. Setting them via `style` lands them on `.EmojiPickerReact`
 * itself, where they actually win.
 */
const EMOJI_PICKER_TOKENS: CSSProperties = {
  // Surface
  "--epr-bg-color": "var(--wx-bg)",
  "--epr-category-label-bg-color": "var(--wx-bg)",
  "--epr-reactions-bg-color": "var(--wx-bg-elevated)",
  "--epr-picker-border-color": "var(--wx-border)",
  "--epr-picker-border-radius": "var(--wx-radius-lg, 12px)",
  // Text
  "--epr-text-color": "var(--wx-fg)",
  "--epr-category-label-text-color": "var(--wx-fg-muted)",
  // Hover / focus
  "--epr-hover-bg-color": "var(--wx-bg-elevated)",
  "--epr-focus-bg-color": "var(--wx-bg-elevated-2)",
  "--epr-highlight-color": "var(--wx-primary)",
  "--epr-category-icon-active-color": "var(--wx-primary)",
  // Search input
  "--epr-search-input-bg-color": "var(--wx-bg-elevated)",
  "--epr-search-input-bg-color-active": "var(--wx-bg-elevated)",
  "--epr-search-input-text-color": "var(--wx-fg)",
  "--epr-search-input-placeholder-color": "var(--wx-fg-muted)",
  "--epr-search-border-color": "var(--wx-border)",
  "--epr-search-border-color-active": "var(--wx-primary)",
  "--epr-search-input-border-radius": "var(--wx-radius, 8px)",
  // Sizing — `--epr-category-navigation-button-size` is the lever
  // for the category-nav icon row (the lib uses it for both the
  // button width AND the sprite background-size + crop offsets, so
  // the icons scale proportionally instead of getting misaligned).
  "--epr-emoji-size": "22px",
  "--epr-emoji-fullsize": "30px",
  "--epr-emoji-padding": "4px",
  "--epr-category-padding": "4px",
  "--epr-category-navigation-button-size": "22px",
  "--epr-category-label-height": "26px",
  "--epr-header-padding": "6px",
  "--epr-search-input-height": "30px",
  // Left padding clears the absolutely-positioned search icon (which
  // sits at `left: var(--epr-search-bar-inner-padding)` + its own
  // 14-ish px width) plus a small breathing gap. Right padding
  // leaves room for the clear-button chip during search.
  "--epr-search-input-padding": "0 32px 0 34px",
  "--epr-horizontal-padding": "8px",
} as CSSProperties;

export function EmojiPicker({ open, onClose, onSelect, anchorRef }: EmojiPickerProps) {
  const t = useTranslations("messages");
  const categories = useMemo(() => buildEmojiCategories(t), [t]);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      // composedPath (not e.target): inside the Shadow-DOM embed a document
      // listener sees the event retargeted to the shadow host, so contains()
      // reads every in-picker click (e.g. a category icon) as "outside" and
      // closes on mousedown before the click lands. composedPath pierces the
      // shadow boundary and stays correct in light DOM too.
      const path = e.composedPath();
      if (pickerRef.current && path.includes(pickerRef.current)) return;
      if (anchorRef?.current && path.includes(anchorRef.current)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      onClose();
      // The search input unmounts with the picker; without this focus falls to <body>, which
      // in the Shadow DOM embeds is outside the widget (a keyboard visitor loses their place).
      // Hand it back to the emoji button, now and again once the library's own focus restore
      // has run after the exit animation.
      anchorRef?.current?.focus({ preventScroll: true });
      restoreFocusIfLost(anchorRef?.current);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={pickerRef}
          initial={{ opacity: 0, y: 6, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 6, scale: 0.96 }}
          transition={{ duration: 0.14, ease: "easeOut" }}
          className={cn(
            "absolute bottom-full left-3 z-20 mb-2 origin-bottom-left",
            "overflow-hidden rounded-wx-lg shadow-lg",
            // The lib's default `--epr-hover-bg-color` (#e5f0fa, pale
            // blue) leaks through on the search-clear chip — it sits
            // on top of the elevated input so visitors see a contrast
            // disc that doesn't match the rest of the picker. Force
            // the hover/focus chrome to inherit the input's surface
            // so the clear button reads as a subtle ghost.
            "[&_.epr-btn-clear-search:hover]:!bg-wx-bg-elevated-2",
            "[&_.epr-btn-clear-search:focus]:!bg-wx-bg-elevated-2",
          )}
          role="dialog"
          aria-label={t("emojiPicker")}
        >
          <Suspense
            fallback={
              <div
                className="bg-wx-bg flex items-center justify-center"
                style={{ width: 320, height: 360 }}
              >
                <div className="border-wx-border border-t-wx-primary h-5 w-5 animate-spin rounded-full border-2" />
              </div>
            }
          >
            <EmojiPickerReact
              onEmojiClick={(d: EmojiClickData) => onSelect(d.emoji)}
              width={320}
              height={360}
              emojiStyle={EMOJI_STYLE_NATIVE}
              previewConfig={{ showPreview: false }}
              skinTonesDisabled
              lazyLoadEmojis
              categories={categories}
              searchPlaceHolder={t("emojiSearch")}
              style={EMOJI_PICKER_TOKENS}
            />
          </Suspense>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
